import { EDeviceCategory } from '../conf/device-categories';
import { EDeviceModel } from '../conf/device-models';
import { Device } from './device';
import {
	AUTOM_FW_UPDATE_IDLE,
	AUTOM_FW_UPDATE_IN_PROGRESS,
	DeviceStatus,
	DeviceStatusUpdate,
	isAutomFwUpdateInProgress,
} from './device-status';

export const isEthernetGatewayModel = (modelId?: number): boolean =>
	modelId === EDeviceModel.GWETH;

export const shouldFetchAutomFwStatus = (
	device: Pick<Device, 'CategoryId' | 'ModelId' | 'RootId'>,
	siteDevices: Pick<Device, 'Id' | 'ModelId'>[]
): boolean => {
	if (isEthernetGatewayModel(device.ModelId)) {
		return true;
	}
	if (device.CategoryId !== EDeviceCategory.Automation || device.RootId == null) {
		return false;
	}
	const root = siteDevices.find(d => d.Id === device.RootId);
	return isEthernetGatewayModel(root?.ModelId);
};

export const shouldShowGwethFwUpdateModal = (
	device: Pick<Device, 'CategoryId' | 'ModelId' | 'RootId'> | undefined,
	status: DeviceStatus | undefined,
	siteDevices: Pick<Device, 'Id' | 'ModelId'>[]
): boolean => {
	if (!device) {
		return false;
	}
	return shouldFetchAutomFwStatus(device, siteDevices) && isAutomFwUpdateInProgress(status);
};

/** Device id for GET /v1/devices/{id}/updates: the device on screen, including a child automation. */
export const firmwareAvailabilityDeviceId = (
	device: Pick<Device, 'Id'>
): number => device.Id;

export const firmwareUpdateTargetDeviceId = (
	device: Pick<Device, 'Id' | 'CategoryId' | 'RootId'>
): number => {
	if (device.CategoryId === EDeviceCategory.Automation && device.RootId != null) {
		return device.RootId;
	}
	return device.Id;
};

const ethernetFamilyRootId = (
	device: Pick<Device, 'Id' | 'RootId' | 'ModelId'>,
	siteDevices: Pick<Device, 'Id' | 'ModelId'>[]
): number | undefined => {
	if (isEthernetGatewayModel(device.ModelId)) {
		return device.Id;
	}
	if (device.RootId == null) {
		return undefined;
	}
	const root = siteDevices.find(d => d.Id === device.RootId);
	return isEthernetGatewayModel(root?.ModelId) ? device.RootId : undefined;
};

export const filterAutomFwUpdatePatch = (
	patch: DeviceStatusUpdate,
	eventDeviceId: number,
	siteDevices: Pick<Device, 'Id' | 'RootId' | 'ModelId'>[]
): DeviceStatusUpdate => {
	if (patch.AutomFwUpdate === undefined) {
		return patch;
	}
	const eventDevice = siteDevices.find(device => device.Id === eventDeviceId);
	if (eventDevice && ethernetFamilyRootId(eventDevice, siteDevices) !== undefined) {
		return patch;
	}
	const filtered = { ...patch };
	delete filtered.AutomFwUpdate;
	return filtered;
};

export const automFwEventAppliesToDevice = (
	eventDeviceId: number,
	current: Pick<Device, 'Id' | 'RootId' | 'ModelId'> | undefined,
	siteDevices: Pick<Device, 'Id' | 'RootId' | 'ModelId'>[]
): boolean => {
	if (!current) {
		return false;
	}
	if (current.Id === eventDeviceId) {
		return true;
	}
	const eventDevice = siteDevices.find(d => d.Id === eventDeviceId);
	if (!eventDevice) {
		return false;
	}
	const currentRoot = ethernetFamilyRootId(current, siteDevices);
	const eventRoot = ethernetFamilyRootId(eventDevice, siteDevices);
	return currentRoot != null && currentRoot === eventRoot;
};

export const mergeAutomFwStatusFlags = (
	...flags: Array<number | undefined | null>
): number | undefined => {
	const present = flags.filter((flag): flag is number => flag !== undefined && flag !== null);
	if (present.length === 0) {
		return undefined;
	}
	return present.some(flag => flag === AUTOM_FW_UPDATE_IN_PROGRESS)
		? AUTOM_FW_UPDATE_IN_PROGRESS
		: AUTOM_FW_UPDATE_IDLE;
};

export const mergeFamilyAutomFwIntoCurrent = (
	currentStatus: DeviceStatus | undefined,
	patch: DeviceStatusUpdate,
	sameDevice: boolean
): DeviceStatus | undefined => {
	if (!currentStatus) {
		return currentStatus;
	}
	if (sameDevice) {
		return { ...currentStatus, ...patch };
	}
	if (patch.AutomFwUpdate === undefined) {
		return currentStatus;
	}
	const next: DeviceStatus = { ...currentStatus, AutomFwUpdate: patch.AutomFwUpdate };
	// TODO: re-enable firmware progress percentage (event 94 / DeviceStatus.Percentage)
	// if (patch.AutomFwUpdate === AUTOM_FW_UPDATE_IDLE) {
	// 	next.Percentage = undefined;
	// }
	return next;
	return next;
};
